import { useCallback, useEffect, useRef, useState } from 'react';
import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useSearchParams from '../../../../../hooks/useSearchParams';
import { NumberRangeFilterTypes } from '../../../../FilterV3/components/NumberRangeFilter/numberRangeFilter.types';
import {
  CleanFilter,
  CommonFilterHookReturn,
  GetSearchParamKeys,
  HandleResetToDefaultValue,
} from '../commonHookReturn.types';
import { MILLISECONDS_TO_APPLY_FILTER } from '../constants';

const isDiffFromDefault = (
  defaultValueToCompare: 'min' | 'max',
  value: number,
  minDefault: number,
  maxDefault: number,
): boolean => {
  if (defaultValueToCompare === 'min') {
    return value !== minDefault;
  }
  return value !== maxDefault;
};

export interface UseNumberRangeFilterParams {
  type: NumberRangeFilterTypes;
  minValueKey: string;
  maxValueKey: string;
  maxAvailableValue: number;
  minAvailableValue?: number;
  defaultMinValue?: number;
  defaultMaxValue?: number;
  shouldIgnoreDefaultFilters?: boolean;
  validate?: (minValue: number, maxValue: number) => boolean;
}

export interface UseNumberRangeFilterOutput extends CommonFilterHookReturn {
  type: NumberRangeFilterTypes;
  maxAvailableValue: number;
  minAvailableValue: number;
  hasSelectedValue: boolean;
  handleUpdateInternalState: (newExpandableState: boolean) => void;
  minValue: number;
  maxValue: number;
  setMinValue: (newMinValue: number) => void;
  setMaxValue: (newMaxValue: number) => void;
  minValueKey: string;
  maxValueKey: string;
}

const useNumberRangeFilter = ({
  type,
  maxValueKey,
  minValueKey,
  maxAvailableValue,
  minAvailableValue,
  defaultMinValue,
  defaultMaxValue,
  shouldIgnoreDefaultFilters = true,
  validate,
}: UseNumberRangeFilterParams): UseNumberRangeFilterOutput => {
  const { searchParams, setSearchParams } = useSearchParams();

  const effectiveMinAvailableValue = minAvailableValue ?? 0;

  const formatDefaultValues = (userDefaultValues: Array<number | null>) => {
    const formattedDefaultValues = userDefaultValues.map((userDefaultValue, index) => {
      if (userDefaultValue == null || !Number.isFinite(+userDefaultValue)) {
        const isMin = index === 0;
        const defaultValue = isMin ? effectiveMinAvailableValue : maxAvailableValue;
        return defaultValue;
      }
      return +userDefaultValue;
    });
    return formattedDefaultValues;
  };

  const getDefaultValues = (): number[] => {
    const defaultValuesFromSearch = [searchParams[minValueKey], searchParams[maxValueKey]];
    const userDefaultValues = shouldIgnoreDefaultFilters
      ? defaultValuesFromSearch
      : [defaultMinValue ?? null, defaultMaxValue ?? null];

    return formatDefaultValues(userDefaultValues);
  };

  const [values, setValues] = useState<number[]>(getDefaultValues);
  const debouncedValues = useDebounce(values, MILLISECONDS_TO_APPLY_FILTER);

  const thresholdsRef = useRef({
    minDefault: effectiveMinAvailableValue,
    maxDefault: maxAvailableValue,
  });

  useEffect(() => {
    const { minDefault, maxDefault } = thresholdsRef.current;
    if (!maxDefault || debouncedValues !== values) {
      return;
    }

    const [minValue, maxValue] = debouncedValues;

    if (validate && !validate(minValue, maxValue)) {
      return;
    }

    const hasMinValue = isDiffFromDefault('min', minValue, minDefault, maxDefault);
    const hasMaxValue = isDiffFromDefault('max', maxValue, minDefault, maxDefault);
    setSearchParams({
      [minValueKey]: hasMinValue ? minValue : undefined,
      [maxValueKey]: hasMaxValue && Number.isFinite(maxValue) ? maxValue : undefined,
    });
  }, [debouncedValues, values, minValueKey, maxValueKey, validate, setSearchParams]);

  useEffect(() => {
    thresholdsRef.current = {
      minDefault: effectiveMinAvailableValue,
      maxDefault: maxAvailableValue,
    };

    if (!maxAvailableValue) {
      return;
    }
    setValues(getDefaultValues());
  }, [maxAvailableValue, effectiveMinAvailableValue]);

  const [hasSelectedValue, setHasSelectedValue] = useState(
    getDefaultValues().some((value, index) =>
      isDiffFromDefault(
        index === 0 ? 'min' : 'max',
        value,
        effectiveMinAvailableValue,
        maxAvailableValue,
      ),
    ),
  );

  const handleUpdateInternalState = useCallback(
    (newExpandableState: boolean) => {
      if (newExpandableState === true) {
        return;
      }
      setHasSelectedValue(
        values.some((value, index) =>
          isDiffFromDefault(
            index === 0 ? 'min' : 'max',
            value,
            effectiveMinAvailableValue,
            maxAvailableValue,
          ),
        ),
      );
    },
    [values, effectiveMinAvailableValue, maxAvailableValue],
  );

  const handleSetValues = useCallback((to: 'min' | 'max', value: number) => {
    const isValidNumber = Number.isFinite(value);
    if (!isValidNumber) {
      return;
    }

    setValues((oldValues) => {
      if (to === 'min') {
        return [value, oldValues[1]];
      }
      return [oldValues[0], value];
    });
  }, []);

  const cleanFilter: CleanFilter = () => {
    setValues([effectiveMinAvailableValue, maxAvailableValue]);
  };
  const getSearchParamKeys: GetSearchParamKeys = () => [minValueKey, maxValueKey];
  const handleResetToDefaultValue: HandleResetToDefaultValue = () => {
    if (defaultMinValue == null && defaultMaxValue == null) {
      cleanFilter();
      return;
    }
    const formattedValues = formatDefaultValues([defaultMinValue ?? null, defaultMaxValue ?? null]);
    setValues(formattedValues);
  };

  return {
    type,
    maxAvailableValue,
    minAvailableValue: effectiveMinAvailableValue,
    hasSelectedValue,
    handleUpdateInternalState,
    minValue: values[0],
    maxValue: values[1],
    setMinValue: (newMinValue: number) => handleSetValues('min', newMinValue),
    setMaxValue: (newMaxValue: number) => handleSetValues('max', newMaxValue),
    minValueKey,
    maxValueKey,
    cleanFilter,
    getSearchParamKeys,
    handleResetToDefaultValue,
  };
};

export default useNumberRangeFilter;
